import { PropsWithChildren } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { useLocation, useParams } from 'react-router-dom';
import { PageLink } from '@/components/TopPageLinks';
import CandidatePagesContext from '@/contexts/candidatePagesContext';
import useApiFetch from '@/hooks/useApiFetch';
import candidatesServices, { CandidateResponse } from '@/services/candidates';
import { chakraExtendedTheme } from '@/styles/theme';
import { FetchApiIds } from '@/types';
import { Paths } from '@/types/routes';
import CandidatePageComponent from './Candidate.component';

const HEADER_LABELS = ['Overview', 'Interviews', 'Jobs', 'Notes'] as const;

type HeaderLinksMap = { [label in (typeof HEADER_LABELS)[number]]: string };

const CANDIDATE_HEADER_LINKS: HeaderLinksMap = {
  Overview: Paths.CANDIDATE_DETAILS,
  Interviews: Paths.CANDIDATE_INTERVIEWS,
  Jobs: Paths.CANDIDATE_JOBS,
  Notes: Paths.CANDIDATE_NOTES,
};

const TALENT_POOL_HEADER_LINKS: HeaderLinksMap = {
  Overview: Paths.TALENT_POOL_DETAILS,
  Interviews: Paths.TALENT_POOL_INTERVIEWS,
  Jobs: Paths.TALENT_POOL_JOBS,
  Notes: Paths.TALENT_POOL_NOTES,
};

type Props = PropsWithChildren<{
  isTalentPool?: boolean;
}>;

const CandidatePage = ({ isTalentPool = false, ...props }: Props): JSX.Element => {
  const { id = '' } = useParams();
  const location = useLocation();

  const headerLinks = isTalentPool ? TALENT_POOL_HEADER_LINKS : CANDIDATE_HEADER_LINKS;

  const topPageLinks: PageLink[] = HEADER_LABELS.map<PageLink>((label) => ({
    label,
    to: {
      path: headerLinks[label],
      params: { id },
    },
    isActived: location.pathname === headerLinks[label].replace(':id', id),
  }));

  const candidateResponse = useApiFetch<CandidateResponse>(
    [FetchApiIds.CANDIDATE_DETAILS, id],
    async () => candidatesServices.getCandidateByID(id)
  );

  const refetchCandidate = (): void => {
    candidateResponse.refetch();
  };

  const isNotesPage = location.pathname === headerLinks.Notes.replace(':id', id);

  return (
    <ChakraProvider theme={chakraExtendedTheme}>
      <CandidatePagesContext.Provider
        value={{
          refetchCandidate,
          candidate: candidateResponse.data?.candidate,
          isLoadingCandidate: candidateResponse.isLoading,
        }}
      >
        <CandidatePageComponent
          fullCandidate={candidateResponse.data?.candidate}
          topPageLinks={topPageLinks}
          refetchCandidateData={refetchCandidate}
          isNotesPage={isNotesPage}
          isTalentPool={isTalentPool}
          {...props}
        />
      </CandidatePagesContext.Provider>
    </ChakraProvider>
  );
};

export default CandidatePage;
